import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Shark — a friendly, grinning shark pup resting on its belly like a plush toy:
// one chunky blue-grey torpedo of a body, a cream underbelly, a big proud
// dorsal fin, stubby side fins and a tall two-lobed tail. The grin is wide and
// harmless — even the teeth are two blunt cream nubs. Mess batch: chunky toy
// proportions, big friendly eyes, warm saturated colours.
//
// Frame: faces +Z (the eyes and grin), rests on the ground at y = 0 (belly
// contact — a beached-but-delighted pup, no water plane). ~1.4 m long, ~0.6 m
// tall to the dorsal tip.
//
// Rig (flat, each named node about its own pivot):
//   - "body"  : the whole torpedo + face + dorsal fin. Breathes up and down.
//   - "tail"  : tail stock + both flukes. Pivot at the tail root, sways
//               side to side — the signature move.
//   - "fin-l" / "fin-r" : the stubby pectoral fins. Pivot at each shoulder.

const GREY = "#6d94c9";        // main hide — a warm, cheerful blue-grey
const GREY_DARK = "#4f719f";   // shading (fins, flukes, dorsal)
const CREAM = "#f6efe3";       // underbelly, eye whites, tooth nubs
const EYE_DARK = "#3b3230";    // pupils + mouth line (warm dark, not black)
const MOUTH = "#c96a6a";       // the grin's inside — a friendly rosy red

// Pivots for the animation tracks.
const TAIL_PIVOT: [number, number, number] = [0, 0.24, -0.46];
const FIN_L: [number, number, number] = [-0.26, 0.16, 0.22];
const FIN_R: [number, number, number] = [0.26, 0.16, 0.22];

function pectoral(name: string, side: -1 | 1): Part {
  // A stubby flattened flipper splayed out and down from the shoulder.
  return {
    geometry: new THREE.SphereGeometry(0.11, 10, 8),
    color: GREY_DARK,
    position: [side * 0.31, 0.13, 0.22],
    scale: [1.6, 0.28, 0.8],
    rotation: [0, side * -0.5, side * -0.35],
    name,
  };
}

export const shark = defineModel({
  id: "shark",
  batchId: "mess",
  name: "Shark",
  tags: ["creature", "mess", "cute", "animated", "sea", "ocean", "shark", "animal"],
  summary: "A friendly grinning shark pup resting on its belly, tail swaying happily.",
  description:
    "A storybook shark pup: a chunky blue-grey torpedo resting belly-down like " +
    "a plush toy, with a cream underbelly, a big proud dorsal fin, stubby side " +
    "fins, a tall two-lobed tail and a wide harmless grin with two blunt tooth " +
    "nubs. The idle breathes and flaps the fins; tail-sway wags the whole tail " +
    "like a happy dog.",
  author: "models",
  footprint: 0.8,
  tiles: { w: 1, h: 2 },
  parts(): Part[] {
    return [
      // ── the body torpedo ─────────────────────────────────────────────────
      // Main mass: one big sphere stretched along Z, bottom kissing the ground.
      { geometry: new THREE.SphereGeometry(0.28, 14, 12), color: GREY,
        position: [0, 0.26, 0.0], scale: [1.0, 0.95, 2.1], name: "body" },
      // Chunky snout boost at the front so the pup reads big-headed.
      { geometry: new THREE.SphereGeometry(0.24, 14, 12), color: GREY,
        position: [0, 0.28, 0.35], scale: [1.05, 0.95, 1.1], name: "body" },
      // Cream underbelly: slightly lower so it shows along the ground contact.
      { geometry: new THREE.SphereGeometry(0.27, 14, 12), color: CREAM,
        position: [0, 0.2, 0.08], scale: [0.92, 0.75, 1.95], name: "body" },

      // ── the face (big, friendly, close together) ─────────────────────────
      // Eye whites.
      { geometry: new THREE.SphereGeometry(0.07, 12, 10), color: CREAM,
        position: [-0.12, 0.36, 0.5], name: "body" },
      { geometry: new THREE.SphereGeometry(0.07, 12, 10), color: CREAM,
        position: [0.12, 0.36, 0.5], name: "body" },
      // Dark pupils, set slightly forward.
      { geometry: new THREE.SphereGeometry(0.036, 10, 8), color: EYE_DARK,
        position: [-0.115, 0.36, 0.555], name: "body" },
      { geometry: new THREE.SphereGeometry(0.036, 10, 8), color: EYE_DARK,
        position: [0.115, 0.36, 0.555], name: "body" },
      // Tiny cream highlights for sparkle.
      { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: CREAM,
        position: [-0.1, 0.377, 0.583], name: "body" },
      { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: CREAM,
        position: [0.13, 0.377, 0.583], name: "body" },
      // The grin: a thin dark smile arc sunk into the snout so only its wide
      // upturned front shows (like the whale's), with a rosy tongue hint
      // tucked just behind the arc.
      { geometry: new THREE.TorusGeometry(0.13, 0.013, 8, 24), color: EYE_DARK,
        position: [0, 0.235, 0.49], rotation: [Math.PI / 2 + 0.3, 0, 0], name: "body" },
      { geometry: new THREE.SphereGeometry(0.055, 10, 8), color: MOUTH,
        position: [0, 0.185, 0.55], scale: [1.5, 0.5, 0.6], name: "body" },
      // Two blunt cream tooth nubs peeking below the grin — harmless.
      { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.03), color: CREAM,
        position: [-0.05, 0.175, 0.595], name: "body" },
      { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.03), color: CREAM,
        position: [0.05, 0.175, 0.595], name: "body" },

      // ── the dorsal fin (big and proud) ───────────────────────────────────
      // A tall flattened cone raked slightly back on the crown.
      { geometry: new THREE.ConeGeometry(0.14, 0.26, 10), color: GREY_DARK,
        position: [0, 0.54, -0.02], scale: [0.5, 1.0, 1.3],
        rotation: [-0.35, 0, 0], name: "body" },

      // ── the tail ─────────────────────────────────────────────────────────
      // Tail stock reaching back out of the body.
      { geometry: new THREE.SphereGeometry(0.11, 10, 8), color: GREY,
        position: [0, 0.22, -0.52], scale: [0.8, 0.75, 1.6], name: "tail" },
      // The tall upper fluke and shorter lower fluke of a shark tail,
      // both flattened cones fanned in the vertical plane.
      { geometry: new THREE.ConeGeometry(0.1, 0.26, 10), color: GREY_DARK,
        position: [0, 0.38, -0.66], scale: [0.28, 1.0, 1.1],
        rotation: [-0.55, 0, 0], name: "tail" },
      { geometry: new THREE.ConeGeometry(0.08, 0.17, 10), color: GREY_DARK,
        position: [0, 0.13, -0.66], scale: [0.28, 1.0, 1.1],
        rotation: [Math.PI + 0.5, 0, 0], name: "tail" },

      // ── the pectoral fins ────────────────────────────────────────────────
      pectoral("fin-l", -1),
      pectoral("fin-r", 1),
    ];
  },
  animations: [
    // 1. Idle — a slow breathing bob of the body while the pectoral fins give
    //    tiny flaps and the tail drifts a touch. Reads as "happy, waiting".
    {
      id: "idle",
      label: "Idle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "body",
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.06, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.06, 0], ease: "sine" },
            { t: 3.2, value: [0, -0.06, 0], ease: "sine" },
          ],
        },
        {
          target: "fin-l", pivot: FIN_L,
          rotation: [
            { t: 0, value: [0, 0, -0.08], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.12], ease: "sine" },
            { t: 3.2, value: [0, 0, -0.08], ease: "sine" },
          ],
        },
        {
          target: "fin-r", pivot: FIN_R,
          rotation: [
            { t: 0, value: [0, 0, 0.08], ease: "sine" },
            { t: 1.6, value: [0, 0, -0.12], ease: "sine" },
            { t: 3.2, value: [0, 0, 0.08], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Tail-sway — the signature move: the whole tail wags side to side
    //    about its root like a happy dog, the body rolling gently in
    //    counter-phase and the fins flicking with each wag.
    {
      id: "tail-sway",
      label: "Tail sway",
      duration: 1.6,
      loop: true,
      tracks: [
        {
          target: "tail", pivot: TAIL_PIVOT,
          rotation: [
            { t: 0, value: [0, -0.5, 0], ease: "easeInOut" },
            { t: 0.8, value: [0, 0.5, 0], ease: "easeInOut" },
            { t: 1.6, value: [0, -0.5, 0], ease: "easeInOut" },
          ],
        },
        {
          // The body counter-rolls a touch so the wag reads through the pup.
          target: "body", pivot: [0, 0.26, 0.1],
          rotation: [
            { t: 0, value: [0, 0.07, 0.03], ease: "easeInOut" },
            { t: 0.8, value: [0, -0.07, -0.03], ease: "easeInOut" },
            { t: 1.6, value: [0, 0.07, 0.03], ease: "easeInOut" },
          ],
        },
        {
          target: "fin-l", pivot: FIN_L,
          rotation: [
            { t: 0, value: [0, 0, -0.15], ease: "sine" },
            { t: 0.8, value: [0, 0, 0.2], ease: "sine" },
            { t: 1.6, value: [0, 0, -0.15], ease: "sine" },
          ],
        },
        {
          target: "fin-r", pivot: FIN_R,
          rotation: [
            { t: 0, value: [0, 0, 0.2], ease: "sine" },
            { t: 0.8, value: [0, 0, -0.15], ease: "sine" },
            { t: 1.6, value: [0, 0, 0.2], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
